import axios from 'axios';
import { getUserToken } from '../utils/utils';
import { captureException } from '@sentry/react';
import {
  refreshAccessToken,
  getRefreshTokenPromise,
  setRefreshTokenPromise,
} from '../utils/refresh-token';

const api = axios.create();

api.interceptors.request.use((config) => {
  const country = localStorage.getItem('country');

  // Reject API call if country is not set
  if (!country) {
    return Promise.reject(
      new Error('X_COUNTRY header is required but not set in localStorage')
    );
  }
  config.headers['Authorization'] = `Bearer ${getUserToken()}`;
  config.headers['X_COUNTRY'] = localStorage.getItem('country');
  config.headers['X_VEHICLE_TYPE'] = localStorage.getItem('vehicleType');
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      let refreshPromise = getRefreshTokenPromise();
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken();
        setRefreshTokenPromise(refreshPromise);
      }

      const refreshed = await refreshPromise;

      if (refreshed) {
        originalRequest.headers['Authorization'] = `Bearer ${getUserToken()}`;
        return api(originalRequest);
      }
    }

    captureException(error);
    return Promise.reject(error);
  }
);

const axiosService = {
  get: (endPoint, data, headers = {}, responseType = null) => {
    const config = {};

    if (!endPoint) {
      throw Error('endPoint is required params');
    } else {
      if (data) {
        config.params = data;
      }

      if (responseType) {
        config.responseType = responseType;
      }

      config.headers = headers;
      return api.get(endPoint, config);
    }
  },

  post: (endPoint, data, headers = {}) => {
    if (!(endPoint || !data)) {
      throw Error('endPoint and data are required params');
    }
    return api.post(endPoint, data, { headers });
  },

  put: (endPoint, data, headers = {}) => {
    if (!(endPoint || !data)) {
      throw Error('endPoint and data are required params');
    }
    return api.put(endPoint, data, { headers });
  },

  delete: (endPoint, data, headers = {}) => {
    const config = {};
    if (!endPoint) {
      throw Error('endPoint is required params');
    } else {
      config.headers = headers;
      return api.delete(endPoint, { data: data }, config);
    }
  },
};

export default axiosService;

const FILE_VERSION = process.env.REACT_APP_FILE_VERSION;
